import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import avatarMap from "../../assets/avatars";
import LanguageModal from "../languageModal";
import NextConsultationCard from "../nextConsultationCard";
import { pathLogin, pathTherapistHome, pathAccount, patientProfileUrl, therapistActivityPlanUrl, patientSummaryUrl, caregiverSupportUrl } from "../../config/constants";
import {
  Sidebar, Nav, NavItem,
  PatientPreview, PatientPreviewAvatar, PatientPreviewCode,
  SidebarBottom, SidebarLink,
} from "./styles";

const IconMenu = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);
const IconProfile = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconCalendar = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconSummary = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);
const IconChat = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const IconSettings = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
const IconAccount = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconLogout = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const IconGlobe = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const AppSidebar = ({ patient, activeNav }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [languageOpen, setLanguageOpen] = useState(false);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    history.push(pathLogin);
  };

  const goToProfile = () => {
    if (patient) history.push(patientProfileUrl(patient.id), { patient });
  };

  return (
    <Sidebar>
      <Nav>
        <NavItem $active={activeNav === "patients"} onClick={() => history.push(pathTherapistHome)}>
          <IconMenu />
          {t("nav.patientsList")}
        </NavItem>

        {patient && (
          <>
            <PatientPreview>
              <PatientPreviewAvatar
                src={avatarMap[patient.avatar_url] || Object.values(avatarMap)[0]}
                alt="avatar"
              />
              <PatientPreviewCode>{patient.patient_code}</PatientPreviewCode>
            </PatientPreview>

            <NavItem $active={activeNav === "profile"} onClick={goToProfile}>
              <IconProfile />
              {t("nav.profile")}
            </NavItem>
            <NavItem
              $active={activeNav === "activityPlan"}
              onClick={() => patient && history.push(therapistActivityPlanUrl(patient.id), { patient })}
            >
              <IconCalendar />
              {t("nav.activityPlan")}
            </NavItem>
            <NavItem
              $active={activeNav === "summary"}
              onClick={() => patient && history.push(patientSummaryUrl(patient.id), { patient })}
            >
              <IconSummary />
              {t("nav.summary")}
            </NavItem>
            <NavItem
              $active={activeNav === "caregiverSupport"}
              onClick={() => patient && history.push(caregiverSupportUrl(patient.id), { patient })}
            >
              <IconChat />
              {t("nav.caregiverSupport")}
            </NavItem>
          </>
        )}
      </Nav>

      <NextConsultationCard
        role="therapist"
        onGoToSupport={(session) => history.push(caregiverSupportUrl(session.patient_id))}
        hideSupportLink={activeNav === "caregiverSupport"}
      />

      <SidebarBottom>
        <SidebarLink onClick={() => setLanguageOpen(true)}>
          <IconSettings />
          {t("nav.settings")}
        </SidebarLink>
        <SidebarLink $active={activeNav === "account"} onClick={() => history.push(pathAccount)}>
          <IconAccount />
          {t("nav.account")}
        </SidebarLink>
        <SidebarLink onClick={logout}>
          <IconLogout />
          {t("nav.logout")}
        </SidebarLink>
        <SidebarLink href="https://vippstar.eu" target="_blank" rel="noreferrer">
          <IconGlobe />
          vippstar.eu
        </SidebarLink>
      </SidebarBottom>

      {languageOpen && <LanguageModal onClose={() => setLanguageOpen(false)} />}
    </Sidebar>
  );
};

export default AppSidebar;
